// W5-2 — SAVE TO MY ROOM, ONE BUTTON, FOUR SURFACES.
//
// Her ruling put the same capability on four different things: a spine on a category shelf, a cover
// on Lala's Bookshelf, the text reader's header and her page-image reader's header. The reason this
// is ONE component rather than four buttons is the reason her three books had none at all — the
// capability was written into the one reader that happened to need it first, and nothing existed to
// carry it anywhere else.
//
// It takes props, holds no state and calls no hook, which is the entry requirement for a gate to
// CALL it and read the tree back (`scripts/w5-saved-books.mjs`): the slug on the element so a
// rendered walk can find the button for a named book, the accessible name a member's screen reader
// hears, and the saved state on the class so a cover can say so without a reload.
import { saveButtonLabel, saveShelfAriaLabel } from "../lib/saved-books";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";

// "reader" wears the full sentence (there is room for it in a header). "shelf" is the chip that sits
// with a spine or a cover, where there is room for a mark and its accessible name only.
export type SaveVariant = "reader" | "shelf";

export function SaveToRoomButton({ slug, title, saved, onToggle, variant = "reader", className, language = DEFAULT_LANGUAGE }: {
  slug: string;
  title: string;
  // `null` is "we have not been told yet" — the button holds still rather than claiming "not saved".
  saved: boolean | null;
  onToggle: (slug: string) => void;
  variant?: SaveVariant;
  className?: string;
  // 14-B7d(2): a DEFAULT, not a required prop, because two gates CALL this component with nothing
  // and read the English back. The words are handed to the shared law as its fallback parameter.
  language?: string;
}) {
  const on = saved === true;
  const base = variant === "reader" ? "reader__save" : "shelf-save";
  const words = { saved: t(language, "save.saved"), add: t(language, "save.add") };
  const aria = { saved: t(language, "save.ariaSaved"), add: t(language, "save.ariaAdd") };
  return (
    <button
      type="button"
      data-save-book={slug}
      className={`${base}${on ? ` ${base}--on is-saved` : ""}${className ? ` ${className}` : ""}`}
      aria-label={saveShelfAriaLabel(title, on, aria)}
      aria-pressed={on}
      title={saveButtonLabel(on, words)}
      disabled={saved === null}
      onClick={(event) => {
        // A chip that sits inside a shelf card must not also open the book it is sitting on.
        event.stopPropagation();
        event.preventDefault();
        onToggle(slug);
      }}
    >
      {variant === "reader" ? saveButtonLabel(on, words) : <span aria-hidden="true">{on ? "✓" : "＋"}</span>}
    </button>
  );
}
